import { useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, VideoCamera, Images, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function EventMemoriesUploader({ eventId, googleAlbum, canManage, onDone }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [video, setVideo] = useState("");
  const [album, setAlbum] = useState(googleAlbum || "");

  const uploadPhotos = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const ids = [];
      for (const f of Array.from(files)) {
        if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} is over 15MB — skipped`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (r.data?.id) ids.push(r.data.id);
      }
      if (ids.length) {
        await api.post(`/events/${eventId}/photos`, { file_ids: ids });
        toast.success(`${ids.length} photo${ids.length > 1 ? "s" : ""} added`);
        onDone?.();
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addVideo = async () => {
    if (!video.trim()) return;
    try { await api.put(`/events/${eventId}/media`, { video_urls: [video.trim()] }); setVideo(""); toast.success("Video link added"); onDone?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const saveAlbum = async () => {
    try { await api.put(`/events/${eventId}/media`, { google_album_url: album.trim() }); toast.success("Album link saved"); onDone?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-dashed border-secondary/40 bg-accent/30 p-5" data-testid="memories-uploader">
      <p className="text-sm font-medium text-primary">Add event memories</p>
      <p className="mt-0.5 text-xs text-muted-foreground">Upload photos everyone can like &amp; comment on{canManage ? ", or link videos and a Google Photos album" : ""}.</p>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="memories-file-input" onChange={(e) => uploadPhotos(e.target.files)} />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button onClick={() => fileRef.current?.click()} disabled={busy} size="sm" variant="secondary" className="rounded-full gap-1.5" data-testid="memories-upload-btn">
          <UploadSimple size={15} /> {busy ? "Uploading…" : "Upload photos"}
        </Button>
      </div>
      {canManage && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div className="flex gap-2">
            <Input value={video} onChange={(e) => setVideo(e.target.value)} placeholder="Video URL (YouTube…)" className="rounded-full" data-testid="memories-video-input" />
            <Button onClick={addVideo} size="icon" variant="outline" className="shrink-0 rounded-full" data-testid="memories-video-add"><VideoCamera size={16} /></Button>
          </div>
          <div className="flex gap-2">
            <Input value={album} onChange={(e) => setAlbum(e.target.value)} placeholder="Google Photos album URL" className="rounded-full" data-testid="memories-album-input" />
            <Button onClick={saveAlbum} size="icon" variant="outline" className="shrink-0 rounded-full" data-testid="memories-album-save"><Images size={16} /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
